function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Unable to convert canvas to blob"));
        return;
      }
      resolve(blob);
    }, type, quality);
  });
}

function dataUrlToBlob(dataUrl) {
  const [header, base64Data] = dataUrl.split(",");

  if (!header || !base64Data) {
    throw new Error("Invalid canvas data URL");
  }

  const mimeMatch = header.match(/^data:(.*?);base64$/);
  const mimeType = mimeMatch?.[1] || "image/png";
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);

  for (let index = 0; index < binaryString.length; index += 1) {
    bytes[index] = binaryString.charCodeAt(index);
  }

  return new Blob([bytes], { type: mimeType });
}

function isPngSignature(bytes) {
  const pngSignature = [137, 80, 78, 71, 13, 10, 26, 10];

  if (!bytes || bytes.length < pngSignature.length) return false;

  return pngSignature.every((value, index) => bytes[index] === value);
}

async function ensureDecodableImage(blob) {
  const objectUrl = URL.createObjectURL(blob);

  try {
    await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("Generated screenshot is not decodable as an image"));
      image.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function normalizeCanvasToPngBlob(canvas) {
  const dataUrl = canvas.toDataURL("image/png");
  const blob = dataUrlToBlob(dataUrl);
  const bytes = new Uint8Array(await blob.arrayBuffer());

  if (!isPngSignature(bytes)) {
    throw new Error("Generated screenshot does not have a valid PNG signature");
  }

  await ensureDecodableImage(blob);

  return new Blob([bytes], { type: "image/png" });
}

function resizeCanvas(canvas, maxWidth, maxHeight) {
  if (canvas.width <= maxWidth && canvas.height <= maxHeight) return canvas;

  const widthScale = maxWidth / canvas.width;
  const heightScale = maxHeight / canvas.height;
  const scale = Math.min(widthScale, heightScale);
  const resizedCanvas = document.createElement("canvas");
  resizedCanvas.width = Math.round(canvas.width * scale);
  resizedCanvas.height = Math.round(canvas.height * scale);

  const ctx = resizedCanvas.getContext("2d");
  ctx.drawImage(canvas, 0, 0, resizedCanvas.width, resizedCanvas.height);

  return resizedCanvas;
}

export async function captureViewport() {
  const { default: html2canvas } = await import("html2canvas");

  const body = document.body;
  const backgroundColor =
    getComputedStyle(body).backgroundColor || "#111111";

  const canvas = await html2canvas(body, {
    useCORS: true,
    allowTaint: false,
    scale: 1,
    backgroundColor,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    width: window.innerWidth,
    height: window.innerHeight,
    x: window.scrollX,
    y: window.scrollY,
    scrollX: window.scrollX,
    scrollY: window.scrollY,
    ignoreElements: (element) =>
      Boolean(element?.closest?.('[data-ai-exclude-capture="true"]')),
  });

  const outputCanvas = resizeCanvas(canvas, 1920, 1080);
  let blob;

  try {
    blob = await normalizeCanvasToPngBlob(outputCanvas);
  } catch {
    // Fallback conservador por si el navegador falla en la ruta dataURL.
    blob = await canvasToBlob(outputCanvas, "image/png");
    await ensureDecodableImage(blob);
  }

  return {
    blob,
    fileName: `current-view-${Date.now()}.png`,
    mimeType: "image/png",
    width: outputCanvas.width,
    height: outputCanvas.height,
  };
}
